import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { financingAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { DollarSign, Shield, Clock, CheckCircle } from 'lucide-react';
import { SEO } from '@/components/SEO';

export default function Financing() {
  const [searchParams] = useSearchParams();
  const unitName = searchParams.get('unit') || '';
  const inventoryId = searchParams.get('id') || '';

  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '', business_type: '',
    years_in_business: '', annual_revenue: '', credit_score_range: '',
    desired_unit: unitName, inventory_id: inventoryId, vehicle_price: '', down_payment: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.years_in_business) payload.years_in_business = parseInt(payload.years_in_business);
      if (payload.down_payment) payload.down_payment = parseFloat(payload.down_payment);
      if (payload.vehicle_price) payload.vehicle_price = parseFloat(payload.vehicle_price);
      await financingAPI.submit(payload);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (err) {
      toast.error('Failed to submit. Please try again.');
    }
    setSubmitting(false);
  };

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="financing-success">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h1 className="text-3xl font-bold uppercase">Application Received</h1>
        <p className="text-zinc-500 mt-3">Thank you! Our financing team will review your application and contact you within 24 hours.</p>
        <p className="text-sm text-zinc-400 mt-2">Reference: You will receive a confirmation email shortly.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="financing-page">
      <SEO
        title="Truck & Trailer Financing | All Credit Types Welcome"
        description="Get pre-approved for commercial truck and trailer financing at Canadian Truck Sales, Mississauga ON. Competitive rates, flexible terms, all credit types welcome including first-time buyers and owner-operators. Fast approvals. Better rates than the competition. Apply online or call (416) 417-2525."
        url="/financing"
        keywords="truck financing Ontario, truck financing Mississauga, truck financing GTA, truck loan Ontario, commercial vehicle financing Canada, semi truck financing, owner operator financing Ontario, fleet financing Ontario, bad credit truck financing Ontario, no credit truck financing, truck lease Ontario, truck financing near me, best truck financing rates Ontario, used truck financing Canada"
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Form */}
        <div className="lg:col-span-2">
          <h1 className="text-3xl md:text-4xl font-bold uppercase text-[#0A0A0A] mb-2">Financing Application</h1>
          <p className="text-zinc-500 mb-8">Complete the form below and our team will get back to you with financing options.</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold uppercase text-zinc-500 mb-3">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input placeholder="Full Name *" required value={form.name} onChange={e => update('name', e.target.value)} data-testid="fin-name" />
                <Input placeholder="Email *" type="email" required value={form.email} onChange={e => update('email', e.target.value)} data-testid="fin-email" />
                <Input placeholder="Phone *" required value={form.phone} onChange={e => update('phone', e.target.value)} data-testid="fin-phone" />
                <Input placeholder="Company Name" value={form.company} onChange={e => update('company', e.target.value)} data-testid="fin-company" />
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase text-zinc-500 mb-3">Business Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select value={form.business_type} onValueChange={v => update('business_type', v)}>
                  <SelectTrigger data-testid="fin-business-type"><SelectValue placeholder="Business Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sole_proprietor">Sole Proprietor</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                    <SelectItem value="corporation">Corporation</SelectItem>
                    <SelectItem value="owner_operator">Owner-Operator</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Years in Business" type="number" value={form.years_in_business} onChange={e => update('years_in_business', e.target.value)} data-testid="fin-years" />
                <Select value={form.annual_revenue} onValueChange={v => update('annual_revenue', v)}>
                  <SelectTrigger data-testid="fin-revenue"><SelectValue placeholder="Annual Revenue" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under_250k">Under $250K</SelectItem>
                    <SelectItem value="250k_500k">$250K - $500K</SelectItem>
                    <SelectItem value="500k_1m">$500K - $1M</SelectItem>
                    <SelectItem value="1m_5m">$1M - $5M</SelectItem>
                    <SelectItem value="over_5m">Over $5M</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={form.credit_score_range} onValueChange={v => update('credit_score_range', v)}>
                  <SelectTrigger data-testid="fin-credit"><SelectValue placeholder="Credit Score Range" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excellent">Excellent (750+)</SelectItem>
                    <SelectItem value="good">Good (700-749)</SelectItem>
                    <SelectItem value="fair">Fair (650-699)</SelectItem>
                    <SelectItem value="rebuilding">Rebuilding (Below 650)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase text-zinc-500 mb-3">Vehicle & Financing</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input placeholder="Desired Vehicle" value={form.desired_unit} onChange={e => update('desired_unit', e.target.value)} data-testid="fin-vehicle" />
                <Input placeholder="Vehicle Price ($)" type="number" value={form.vehicle_price} onChange={e => update('vehicle_price', e.target.value)} data-testid="fin-vehicle-price" />
                <Input placeholder="Down Payment ($)" type="number" value={form.down_payment} onChange={e => update('down_payment', e.target.value)} data-testid="fin-down-payment" />
              </div>
              <Textarea placeholder="Additional Comments" className="mt-4" value={form.message} onChange={e => update('message', e.target.value)} data-testid="fin-message" />
            </div>

            <Button type="submit" className="bg-[#E31937] hover:bg-[#C4122C] text-white font-bold uppercase h-12 px-10" disabled={submitting} data-testid="fin-submit">
              {submitting ? 'Submitting...' : 'Submit Application'}
            </Button>
          </form>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card className="border-none shadow-sm bg-zinc-50">
            <CardContent className="p-5 space-y-4">
              {[
                { icon: Clock, title: '24-Hour Response', desc: 'Get a response from our financing team within one business day.' },
                { icon: Shield, title: 'All Credit Welcome', desc: 'We work with multiple lenders to find you the best rate, regardless of credit history.' },
                { icon: DollarSign, title: 'Competitive Rates', desc: 'Access to the best rates from our network of trusted Canadian lenders.' },
              ].map(f => (
                <div key={f.title} className="flex gap-3">
                  <div className="w-9 h-9 bg-[#E31937]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <f.icon className="w-4 h-4 text-[#E31937]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{f.title}</p>
                    <p className="text-xs text-zinc-500">{f.desc}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-none shadow-sm bg-[#0A0A0A] text-white">
            <CardContent className="p-5">
              <p className="text-sm font-semibold mb-2">Need help?</p>
              <p className="text-zinc-400 text-xs">Call us directly at</p>
              <a href="tel:+14164172525" className="text-[#E31937] font-bold text-lg">(416) 417-2525</a>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
